import { Button } from "@/components/ui/button";
import { Trophy, ArrowRight, RotateCcw } from "lucide-react";
import { HomeDecisionEntry, fmt } from "@/hooks/useHomeBuyingSim";
import ModuleFeedbackCard from "@/components/ai/ModuleFeedbackCard";

interface Recap {
  totalWallet: number;
  totalScore: number;
  readiness: string;
  strongest: HomeDecisionEntry | null;
  weakest: HomeDecisionEntry | null;
  tradeoff: string;
  takeaway: string;
  nextAction: string;
}

interface Props {
  purchaseCompleted: boolean;
  onDashboard: () => void;
  onAccounts: () => void;
  onTryAgain?: () => void;
  decisionLog?: HomeDecisionEntry[];
  recap?: Recap | null;
  homeName?: string | null;
  affordabilityRating?: string | null;
}

export default function HomeCompletionScreen({
  purchaseCompleted, onDashboard, onAccounts, onTryAgain,
  decisionLog = [], recap, homeName, affordabilityRating,
}: Props) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] py-6">
      <div className="glass-card glow-border max-w-md w-full p-6 sm:p-8 text-center space-y-5">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto animate-pulse">
          <Trophy className="w-10 h-10 text-primary" />
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground">Module Complete!</h2>
        {homeName && purchaseCompleted && (
          <p className="font-heading font-semibold text-foreground">{homeName}</p>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed">
          {purchaseCompleted
            ? "You completed the Homeownership simulation and purchased a home! Your financial profile has been updated."
            : "You completed the Homeownership simulation and learned how major home buying choices affect both your cash and your long-term finances."
          }
        </p>

        {recap && (
          <div className="space-y-3 text-left">
            <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-primary/80">Readiness label</p>
              <p className="font-heading font-bold text-base text-foreground">{recap.readiness}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Wallet impact</p>
                <p className={`font-heading font-bold ${recap.totalWallet < 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {recap.totalWallet < 0 ? '−' : '+'}{fmt(Math.abs(recap.totalWallet))}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Judgment score</p>
                <p className="font-heading font-bold text-foreground">{recap.totalScore >= 0 ? '+' : ''}{recap.totalScore}</p>
              </div>
            </div>
            {recap.strongest && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-500/80">Strongest decision</p>
                <p className="text-sm font-semibold text-foreground">{recap.strongest.label}: {recap.strongest.choice}</p>
                <p className="text-xs text-muted-foreground italic">{recap.strongest.lumiSignal}</p>
              </div>
            )}
            {recap.weakest && recap.weakest !== recap.strongest && (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-500/80">Weakest decision</p>
                <p className="text-sm font-semibold text-foreground">{recap.weakest.label}: {recap.weakest.choice}</p>
                <p className="text-xs text-muted-foreground italic">{recap.weakest.lumiSignal}</p>
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Key tradeoff</p>
              <p className="text-sm text-foreground">{recap.tradeoff}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Real-world takeaway</p>
              <p className="text-sm text-foreground">{recap.takeaway}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Suggested next mission</p>
              <p className="text-sm text-foreground">{recap.nextAction}</p>
            </div>
            {decisionLog.length > 0 && (
              <details className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <summary className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer">
                  Full decision log ({decisionLog.length})
                </summary>
                <ul className="space-y-2 pt-2">
                  {decisionLog.map((d, i) => (
                    <li key={i} className="text-xs border-l-2 border-border pl-2">
                      <p className="font-semibold text-foreground">{d.label} — {d.choice}</p>
                      <p className="text-muted-foreground">{d.consequence}</p>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <ModuleFeedbackCard
          moduleName="Home Buying Simulator"
          moduleKey="home_buying_sim"
          finalMetrics={{
            purchase_completed: purchaseCompleted,
            home_name: homeName,
            affordability_rating: affordabilityRating,
            decision_count: decisionLog.length,
            total_wallet_impact: recap?.totalWallet,
            total_score: recap?.totalScore,
            readiness: recap?.readiness,
          }}
          outcome={purchaseCompleted ? (recap && recap.totalScore >= 12 ? 'strong' : 'moderate') : 'walked_away'}
        />

        <div className="flex flex-col gap-3">
          <Button onClick={onDashboard} className="w-full gap-2">Return to Dashboard <ArrowRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={onAccounts} className="w-full">View My Accounts</Button>
          {onTryAgain && (
            <Button variant="outline" onClick={onTryAgain} className="w-full gap-2">
              <RotateCcw className="w-4 h-4" /> Start a New Run
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
